<template>
  <div>
    <div class="pb-4">
      <span class="text-chips md:block md:text-center">Paso Tres: </span>
      <h2 class="headline-sm pb-1 md:headline-md md:text-center">Experiencia Laboral</h2>
      <p class="subhead-2 text-on-surface-variant md:subhead-1 md:text-center">
        Ingresa tu experiencia laboral y habilidades
      </p>
    </div>
    <div class="flex flex-col gap-4">
      <CvSectionCrud
        :content="job_experience_records_transformed"
        title="Experiencia laboral"
        contentKey="job_experience_records"
        @edit-record="editRecord"
        @add-record="addRecord"
      />
      <section class="my-4">
        <h3 class="headline-5 md:headline-sm">Habilidades</h3>
        <Divider class="mx-0" />
        <form
          @submit.prevent="addSkill"
          class="flex flex-col gap-1 items-center my-4 sm:gap-4 sm:flex-row"
        >
          <FormInputText
            id="skill_input"
            name="skill_input"
            class="w-full"
            placeholder="Ej: Excel avanzado, Marketing digital, Power BI..."
          />
          <Button type="submit" class="btn-outlined mb-2 shrink-0" icon="add">Agregar</Button>
        </form>
        <div v-if="skills_records && skills_records.length > 0" class="flex flex-wrap gap-1">
          <ChipListItem
            v-for="(skill, key) in skills_records"
            :key="key"
            configuration="outlined"
            size="normal"
            icon="close"
            icon-direction="right"
            icon-is-action
            @click-icon="deleteSkill(key)"
          >
            {{ skill.title }}
          </ChipListItem>
        </div>
      </section>
      <section>
        <h3 class="headline-5 md:headline-sm">Curriculum Vitae</h3>
        <Divider class="mx-0 " />
        <div class="p-4 text-center mb-2">
          <Illustration name="add-files" class="w-full max-h-36 p-2" />
          <p class="title-lg text-center text-primary mb-1">¡Agrega tu curriculum vitae!</p>
          <p class="title-sm text-neutral40 mb-4">
            Puedes agregar un archivo en formato PDF. El tamaño máximo admitido es de 5 MB.
          </p>
          <FormInputFile
            accept=".pdf"
            show-file-size
            :max-file-size="2242880"
            @update-files="files = $event"
          ></FormInputFile>
        </div>
      </section>
    </div>
    <modal
      name="form-modal"
      height="auto"
      :clickToClose="false"
      :reset="true"
      :adaptive="true"
      :scrollable="true"
      classes=" !surface-1 md:!rounded-xl !overflow-y-auto"
    >
      <JobExperienceForm
        v-if="contentKey === 'job_experience_records'"
        :previousForm="recordToEdit !== null ? recordToEdit : {}"
        @close="
          $modal.hide('form-modal')
          resetModalData()
        "
        @emit-record="saveRecord('job_experience_records', $event)"
      />
    </modal>
  </div>
</template>

<script>
import { validateRequiredKeys } from '@/utilities.helpers.js'
import Button from '@/Components/Button'
import Divider from '@/Components/Divider'
import ChipListItem from '@/Components/ChipListItem'
import FormInputText from '@/Components/FormInputText'
import FormInputFile from '@/Components/FormInputFile'
import Illustration from '@/Components/Illustration'

export default {
  name: 'CreateProfile_StepThree',
  components: {
    Button,
    Divider,
    ChipListItem,
    FormInputText,
    FormInputFile,
    Illustration,
    CvSectionCrud: () => import('@/Components/CrearPerfil/CvSectionCrud.vue'),
    JobExperienceForm: () => import('@/Components/Perfil/JobExperienceForm.vue'),
  },
  props: {
    config: {
      type: Object,
      validator: value => {
        return validateRequiredKeys(value, ['form_data'])
      },
    },
  },
  data() {
    return {
      recordToEdit: null,
      recordToEditKey: null,
      contentKey: '',
      job_experience_records: this.config.form_data.job_experience,
      skills_records: this.config.form_data.skills,
      files: [],
    }
  },
  methods: {
    deleteSkill(key) {
      this.$swal
        .fire({
          title: 'Confirmación:',
          text: '¿Estás seguro que quieres borrar ese dato?',
          showConfirmButton: true,
          showCancelButton: true,
        })
        .then(result => {
          if (result.isConfirmed) this.skills_records.splice(key, 1)
        })
    },
    addSkill(event) {
      const form = new FormData(event.target)
      const submitValue = form.get('skill_input')
      // check for empty value
      if (!!submitValue) this.skills_records.push({ title: submitValue })
    },
    editRecord(contentKey, recordKey) {
      this.contentKey = contentKey
      this.recordToEdit = this[contentKey][recordKey]
      this.recordToEditKey = recordKey
      this.$modal.show('form-modal')
    },
    addRecord(contentKey) {
      this.contentKey = contentKey
      this.$modal.show('form-modal')
    },
    saveRecord(contentKey, recordData) {
      if (this.recordToEditKey === null) this[contentKey].push(recordData)
      else this.updateContent(contentKey, this.recordToEditKey, recordData)
      this.resetModalData()
      this.$modal.hide('form-modal')
    },
    resetModalData() {
      this.contentKey = ''
      this.recordToEdit = null
      this.recordToEditKey = null
    },
    updateContent(contentKey, contentRecordKey, updatedData) {
      this.$set(this[contentKey], contentRecordKey, updatedData)
    },
  },
  computed: {
    job_experience_records_transformed() {
      return this.job_experience_records.length > 0
        ? this.job_experience_records.map(item => {
            return {
              title: item.job_title,
              subtitle: item.company_name,
              description: item.description,
              initDate: item.start_date,
              finishDate: item.is_currently_active ? 'Actualidad' : item.finish_date,
            }
          })
        : []
    },
  },
  watch: {
    job_experience_records: {
      deep: true,
      handler(value) {
        this.$emit('update-content', 'job_experience', value)
      },
    },
    skills_records: {
      deep: true,
      handler(value) {
        this.$emit('update-content', 'skills', value)
      },
    },
    files: {
      deep: true,
      handler(value) {
        this.$emit('update-content', 'cv_file', value[0])
      },
    },
  },
}
</script>
